import React from 'react';
import { Drawer, Button } from 'antd';
import { FilterOutlined, ClearOutlined } from '@ant-design/icons';
import useIsMobile from '../../hooks/useIsMobile';

/**
 * MobileFilterDrawer - A reusable drawer component for mobile filters
 * On mobile: Shows as a bottom drawer (fully visible)
 * On desktop: Renders children inline (no drawer)
 *
 * @param {Object} props
 * @param {boolean} props.visible - Drawer visibility (only used on mobile)
 * @param {Function} props.onClose - Close handler
 * @param {Function} props.onApply - Apply filters handler (optional, defaults to onClose)
 * @param {Function} props.onClear - Clear all filters handler (optional)
 * @param {React.ReactNode} props.children - Filter form content
 * @param {string} props.title - Drawer title (default: "Filters")
 * @param {number} props.activeFiltersCount - Number of active filters (for badge)
 */
const MobileFilterDrawer = ({
  visible,
  onClose,
  onApply,
  onClear,
  children,
  title = "Filters",
  activeFiltersCount = 0
}) => {
  const isMobile = useIsMobile();

  const handleApply = () => {
    if (onApply) {
      onApply();
    }
    onClose();
  };

  // On mobile, show drawer
  if (isMobile) {
    return (
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FilterOutlined />
            <span>{title}</span>
            {activeFiltersCount > 0 && (
              <span style={{
                background: '#1890ff',
                color: 'white',
                padding: '2px 8px',
                borderRadius: 10,
                fontSize: 12
              }}>
                {activeFiltersCount}
              </span>
            )}
          </div>
        }
        placement="bottom"
        open={visible}
        onClose={onClose}
        height="85vh"
        styles={{
          body: {
            padding: '16px',
            paddingBottom: '80px',
            overflowY: 'auto'
          },
          footer: {
            padding: '12px 16px',
            borderTop: '1px solid #f0f0f0'
          }
        }}
        footer={
          <div style={{ display: 'flex', gap: 12 }}>
            {onClear && (
              <Button
                icon={<ClearOutlined />}
                onClick={onClear}
                style={{ flex: 1 }}
              >
                Clear All
              </Button>
            )}
            <Button
              type="primary"
              onClick={handleApply}
              style={{ flex: 1 }}
            >
              Apply Filters
            </Button>
          </div>
        }
      >
        {children}
      </Drawer>
    );
  }

  // On desktop, render children directly (no drawer)
  return <>{children}</>;
};

/**
 * MobileFilterButton - Button to open filter drawer on mobile
 */
export const MobileFilterButton = ({ onClick, activeFiltersCount = 0, size = "middle" }) => {
  return (
    <Button
      icon={<FilterOutlined />}
      onClick={onClick}
      type={activeFiltersCount > 0 ? 'primary' : 'default'}
      size={size}
    >
      Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
    </Button>
  );
};

export default MobileFilterDrawer;
